// react
import { useCallback, useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
// EnergyRichTreeView.jsx
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import { Avatar, Badge, Box, Stack, Tooltip, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// feature-utils
import { convertTimestampsSummaryToRange } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

// components
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

import { DateTime } from 'luxon';

function EndStatusBadge({ outOfBounds, timestampsSummary, warningSource }) {
  const { t } = useTranslation('energyxplorer');
  const badgeIcon = outOfBounds ? (
    <WarningRoundedIcon fontSize="small" color="warning" />
  ) : (
    <CheckCircleRoundedIcon fontSize="small" color="success" />
  );

  const timeRange = convertTimestampsSummaryToRange(timestampsSummary);
  const isSameDateRange = timeRange.start.hasSame(timeRange.end, 'day');
  const timeRangeString = `${timeRange.start.setLocale('da').toLocaleString(DateTime.DATE_SHORT)} - ${timeRange.end.setLocale('da').toLocaleString(DateTime.DATE_SHORT)}`;

  const tooltipText = !outOfBounds
    ? t('common.timestamp_summary_ok_with_range', {
        timeRange: timeRangeString,
      })
    : warningSource === 'descendant'
      ? t('common.timestamp_summary_warning_in_descendants')
      : isSameDateRange
        ? t('common.timestamp_summary_no_valid_period')
        : t('common.timestamp_summary_warning_with_range', {
            timeRange: timeRangeString,
          });

  return (
    <Tooltip title={<Box whiteSpace={'pre-line'}>{tooltipText}</Box>}>
      <Badge
        badgeContent={badgeIcon}
        sx={{ ml: 1 }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <CalendarTodayRoundedIcon fontSize="small" />
      </Badge>
    </Tooltip>
  );
}

function StartTypeAvatar({ type }) {
  return (
    <Avatar
      variant="rounded"
      sx={{
        backgroundColor: getTypeColor(type) || '#555',
        color: '#fff',
        fontWeight: 'bold',
        height: 32,
        width: 32,
        fontSize: 16,
      }}
    >
      {/* FontAwesome icon class from your helper */}
      <i className={`fas ${getTypeIcon(type) || 'fa-circle'}`}></i>
    </Avatar>
  );
}

function applyBranchWarningState(node) {
  const children = Array.isArray(node.children)
    ? node.children.map(applyBranchWarningState)
    : node.children;
  const childHasWarning = Array.isArray(children)
    ? children.some((child) => child.hasBranchWarning)
    : false;
  const hasOwnWarning = !!node.isOutOfBounds;
  const hasBranchWarning = hasOwnWarning || childHasWarning;
  const warningSource = hasOwnWarning
    ? 'self'
    : childHasWarning
      ? 'descendant'
      : 'none';

  return {
    ...node,
    children,
    hasBranchWarning,
    warningSource,
  };
}

function NodeLabel({ node }) {
  const labelText = node.label ?? `Node ${node.id}`;
  const outOfBounds = !!node.hasBranchWarning;
  const warningSource = node.warningSource;
  const timestampsSummary = node.energySummary.TimestampsSummary;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ py: 0.5, pr: 0.5, minHeight: 36 }}
    >
      <StartTypeAvatar
        type={node.type} // adapt if your type is elsewhere
      />

      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {labelText}
      </Typography>

      <Box sx={{ flex: 1 }} />

      {/* trailing calendar + conditional badge (success/warning) */}
      <EndStatusBadge
        outOfBounds={outOfBounds}
        timestampsSummary={timestampsSummary}
        warningSource={warningSource}
      />
    </Stack>
  );
}

function renderTree({ node, getTypeColor, getTypeIcon }) {
  return (
    <TreeItem
      key={node.id}
      itemId={String(node.id)}
      label={
        <NodeLabel
          node={node}
          getTypeColor={getTypeColor}
          getTypeIcon={getTypeIcon}
        />
      }
      sx={{
        '& .MuiTreeItem-content': {
          // remove default label padding to let our custom label control spacing
          paddingY: 0,
        },
      }}
    >
      {Array.isArray(node.children) &&
        node.children.map((child) =>
          renderTree({ node: child, getTypeColor, getTypeIcon }),
        )}
    </TreeItem>
  );
}

/**
 * EnergyRichTreeView
 *
 * Props:
 * - items: array of nodes [{ id, label, children?, energySummary?, isOutOfBounds? }]
 * - getTypeColor(type): fn -> color string
 * - getTypeIcon(type): fn -> FontAwesome class (e.g. "fa-bolt")
 * - defaultExpanded: array of node ids to expand initially (optional)
 * - selectedId: controlled selected id (optional)
 * - onSelect(id): selection callback (optional)
 */
export default function EnergyRichTreeView({
  items = [],
  defaultExpanded = [],
  selectedId,
  onSelect,
}) {
  const itemsWithBranchWarnings = items.map(applyBranchWarningState);

  const expanded = useMemo(
    () => defaultExpanded.map(String),
    [defaultExpanded],
  );

  const handleSelect = useCallback(
    (event, nodeId) => {
      onSelect && onSelect(nodeId);
    },
    [onSelect],
  );

  return (
    <Box sx={{ minWidth: 600 }}>
      <SimpleTreeView
        defaultExpandedItems={expanded}
        selected={selectedId != null ? String(selectedId) : undefined}
        onSelect={handleSelect}
        sx={{
          '& .MuiTreeItem-group': {
            ml: 2,
            borderLeft: (theme) => `1px dashed ${theme.palette.divider}`,
          },
        }}
      >
        {itemsWithBranchWarnings.map((n) =>
          renderTree({ node: n, getTypeColor, getTypeIcon }),
        )}
      </SimpleTreeView>
    </Box>
  );
}
